/**
 * Skeleton shimmer + fade-in para imágenes de tarjetas (producto / colección / blog).
 *
 * Mientras la imagen no ha cargado, el contenedor muestra un shimmer (definido en
 * base.css). Al cargar, se añade la clase `media-loaded` al contenedor: el CSS quita
 * el shimmer y la imagen entra con un fade (animación CSS).
 *
 * DISEÑO A PRUEBA DE BLANCOS: este script NUNCA modifica la opacidad de las imágenes.
 * Las <img> están siempre visibles (opacity:1 por defecto). Lo único que hace es
 * AÑADIR la clase `media-loaded` para quitar el shimmer. Por tanto, aunque este script
 * falle o se pierda un evento `load`, ninguna imagen puede quedar en blanco: como mucho
 * persistiría el shimmer detrás de una imagen opaca (invisible). Además, un timeout de
 * seguridad revela cualquier contenedor pendiente para que el shimmer nunca se quede fijo.
 */

const WRAPPER_SELECTOR = '.card-gallery, .resource-image:not(.resource-image--placeholder)';

/** Quita el shimmer del contenedor. */
function reveal(wrapper) {
  if (wrapper && !wrapper.classList.contains('media-loaded')) {
    wrapper.classList.add('media-loaded');
  }
}

/** Revela el contenedor si ya no hay nada que esperar (sin img, o alguna ya cargada). */
function check(wrapper) {
  if (wrapper.classList.contains('media-loaded')) return;

  const images = wrapper.querySelectorAll('img');

  // Sin <img> (p.ej. placeholder SVG): nada que esperar.
  if (images.length === 0) {
    reveal(wrapper);
    return;
  }

  // Si alguna imagen del contenedor ya está cargada, quitamos el shimmer.
  for (const img of images) {
    if (img.complete && img.naturalWidth > 0) {
      reveal(wrapper);
      return;
    }
  }
}

/**
 * Fade-in suave para imágenes lazy que NO son de tarjetas (banners, medios de
 * sección, logos del header…), que no tienen shimmer y hacían pop-in de golpe.
 *
 * Usa un listener `load` A NIVEL DE ELEMENTO (fiable; el `load` de una <img> no
 * llega a window ni en captura, así que el listener global no sirve para esto).
 * Solo engancha imágenes que AÚN NO cargaron: las ya completas (cacheadas o
 * above-the-fold) se muestran sin animación para no dar flash. Mismo diseño a
 * prueba de blancos: la <img> es opacity:1 por defecto; la clase solo dispara una
 * animación de una vez.
 */
function bindLazyFade(img) {
  if (img.dataset.fadeBound || img.closest(WRAPPER_SELECTOR)) return;
  img.dataset.fadeBound = '1';
  if (img.complete) return;
  img.addEventListener('load', () => img.classList.add('img-fade-in'), { once: true });
}

/** Recorre el DOM (o un subárbol) revelando lo que ya esté listo. */
function scan(root = document) {
  root.querySelectorAll(WRAPPER_SELECTOR).forEach(check);
  root.querySelectorAll('img[loading="lazy"]').forEach(bindLazyFade);
}

// load/error no burbujean → escucha en fase de captura para pillar cualquier <img>.
function onMediaEvent(event) {
  const img = event.target;
  if (img.tagName !== 'IMG') return;
  reveal(img.closest(WRAPPER_SELECTOR));
}

addEventListener('load', onMediaEvent, true);
addEventListener('error', onMediaEvent, true);

// Revela lo ya cargado al inicializar.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scan());
} else {
  scan();
}

// Rescan al terminar de cargar la página: cubre imágenes cacheadas insertadas tarde
// (Section Rendering API, quick-add, filtros) que no dispararon `load` observable.
addEventListener('load', () => scan(), { once: true });
